import React, { useCallback, useMemo } from "react";
import range from "lodash.range";

import { OutputRow as SessionTypesData } from "api/getSessionTypes";
import ModalFieldText from "components/ModalFieldText";
import { OnCompleteOutput } from "components/ModalWizard/ModalWizard";
import ModalFieldTextArea from "components/ModalFieldTextArea";
import EditableTable from "components/EditableTable";
import { formatSessionDuration } from "services/formatSessionDuration";
import { ValidationCallback } from "validation/validate";
import { useStickySort } from "hooks/useStickySort";
import { useAddActionButtonDelete } from "hooks/useAddActionButtonDelete";
import StepSessionDescription from "./NewRowWizard/StepSessionDescription";
import StepSessionTitle from "./NewRowWizard/StepSessionTitle";
import { columnDefinitionIsEnabled } from "components/EditableTable/columnDefinitionIsEnabled";

interface SessionTypesTableProps {
	sessionTypes: SessionTypesData[];
	isLoading: boolean;
	isWaiting: boolean;
	isError: boolean;
	waitModalTitle: string;
	waitModalIsShowing: boolean;
	onEdit: (
		rows: { old?: SessionTypesData; new?: SessionTypesData }[]
	) => OnCompleteOutput;
}

export type DataInAnyRow = SessionTypesData;

const SessionTypesTable: React.FunctionComponent<SessionTypesTableProps> = ({
	sessionTypes,
	isLoading,
	isWaiting,
	isError,
	onEdit: onEditGlobal,
	waitModalTitle,
	waitModalIsShowing
}) => {
	const onNewRowWizardComplete = useCallback(
		(data: SessionTypesData) => {
			return onEditGlobal([
				{
					new: {
						...data,
						isEnabled: true
					}
				}
			]);
		},
		[onEditGlobal]
	);

	const sortedSessionTypes = useStickySort(
		sessionTypes,
		useCallback((a: SessionTypesData, b: SessionTypesData) => {
			return a.sessionTitle.localeCompare(b.sessionTitle);
		}, []),
		"series"
	);

	const rows = useAddActionButtonDelete(sortedSessionTypes, onEditGlobal);

	const newRowWizard = useMemo(() => {
		const steps = [
			{
				key: "session-title",
				title: "Please give the session a name (e.g. 'conversation practise').",
				shortTitle: "Session title",
				component: StepSessionTitle
			},
			{
				key: "session-description",
				title:
					"Please write a few words to describe what will happen at the session.",
				shortTitle: "Session description",
				component: StepSessionDescription
			}
		];
		const validateNewRowData: ValidationCallback<DataInAnyRow> = (
			data,
			{ checkField }
		) => {
			checkField("sessionTitle", { type: "string" });
			checkField("sessionDescription", { type: "string" });
		};
		return {
			steps,
			validateNewRowData,
			initialData: {}
		};
	}, []);

	const onOKSimpleTextField = useCallback(
		(
			newValue: string,
			row: SessionTypesData,
			rowKey: keyof SessionTypesData,
			onEdit: typeof onEditGlobal
		) => {
			const newData = {
				...row,
				[rowKey]: newValue
			};
			onEdit([{ old: row, new: newData }]);
		},
		[]
	);

	return (
		<>
			<EditableTable<DataInAnyRow>
				allowDeleteRows
				rows={rows}
				onEdit={onEditGlobal}
				dataDescriptionSingular="session type"
				dataDescriptionPlural="session sessionTypes"
				onNew={onNewRowWizardComplete}
				newRowWizard={newRowWizard}
				rowIsDisabled={row => !row.isEnabled}
				isLoading={isLoading}
				isWaiting={isWaiting}
				isError={isError}
				waitModalTitle={waitModalTitle}
				waitModalIsShowing={waitModalIsShowing}
				cols={[
					{
						key: "sessionTitle",
						heading: "Title",
						field: ({ row, rowKey, isEditing, onEdit }) => (
							<ModalFieldText
								isUnlocked={isEditing}
								title="Session title"
								onOK={newValue =>
									onOKSimpleTextField(newValue, row, rowKey, onEdit)
								}
								value={row.sessionTitle}
							/>
						)
					},
					{
						key: "sessionDescription",
						heading: "Description",
						field: ({ row, rowKey, isEditing, onEdit }) => (
							<ModalFieldTextArea
								isUnlocked={isEditing}
								title="Session description"
								onOK={newValue =>
									onOKSimpleTextField(newValue, row, rowKey, onEdit)
								}
								value={row.sessionDescription}
							/>
						)
					},
					columnDefinitionIsEnabled({ initialValue: row => !!row.isEnabled })
				]}
			/>
		</>
	);
};

export default SessionTypesTable;

export function getSingleSessionPriceOptions(selectedValue?: number) {
	return range(3, 7).map(step => {
		const sessionDurationMinutes = step * 10;
		return {
			id: sessionDurationMinutes + "",
			value: sessionDurationMinutes,
			text: formatSessionDuration({ sessionDurationMinutes }),
			selected: !!selectedValue && selectedValue === sessionDurationMinutes
		};
	});
}
